// Schema-driven helpers for the PackEditor tabs (PR 3 Week 2).
//
// Single source of truth: schema/pack_v1.schema.json (lives at the
// repo root; copy under app/frontend-v2/src/lib/schema/ so Vite can
// import it as a module). The backend's app/services/pack_validator.py
// reads the same file via the Python jsonschema library, so anything
// rendered here lines up with what the route accepts.
//
// Helpers below mirror app/services/pack_schema_loader.py so the
// frontend doesn't have to re-derive the same lookups (board enums,
// metric enums, classification enums, required fields).

import schemaJson from "./pack_v1.schema.json";

// ajv is bundled lazily — only ValidateTab needs it. Static type
// inference for the schema isn't worth the complexity here; we treat
// the schema as a runtime artefact + use TS shapes where they help.
export type Schema = typeof schemaJson;
export const schema = schemaJson as Schema & { $defs: Record<string, unknown>; properties: Record<string, unknown> };


function defFor(name: string): Record<string, unknown> {
  const defs = (schema.$defs ?? {}) as Record<string, Record<string, unknown>>;
  const def = defs[name];
  if (!def) {
    throw new Error(
      `No $def named '${name}'. Available: ${Object.keys(defs).join(", ")}`,
    );
  }
  return def;
}


function enumValues(defName: string, propertyPath: string): string[] {
  let node: Record<string, unknown> = defFor(defName);
  for (const part of propertyPath.split(".")) {
    const props = (node.properties ?? {}) as Record<string, Record<string, unknown>>;
    if (!props[part]) {
      throw new Error(
        `Property '${propertyPath}' not found in $def '${defName}'`,
      );
    }
    node = props[part];
  }
  const e = node.enum;
  return Array.isArray(e) ? (e as string[]) : [];
}


export function boardCanonicalOptions(): string[] {
  return enumValues("tableau_next_board", "source_canonical");
}

export function metricCanonicalOptions(): string[] {
  return enumValues("tableau_next_metric", "source_canonical");
}

export function pulseClassificationOptions(): string[] {
  return enumValues("cloud_pulse_metric", "classification");
}

export function permissionPrincipalTypeOptions(): string[] {
  return enumValues("permission_grant", "principal_type");
}

export function permissionAccessLevelOptions(): string[] {
  return enumValues("permission_grant", "access_level");
}

export function pulseSubscriptionChannelOptions(): string[] {
  return enumValues("cloud_pulse_metric", "subscription_channel");
}

// Description text for the tabs to show as a hint
export function descriptionFor(defName: string, propertyPath: string): string | undefined {
  let node: Record<string, unknown> = defFor(defName);
  for (const part of propertyPath.split(".")) {
    const props = (node.properties ?? {}) as Record<string, Record<string, unknown>>;
    if (!props[part]) return undefined;
    node = props[part];
  }
  const desc = node.description;
  return typeof desc === "string" ? desc : undefined;
}
